import React, { useState, useEffect } from 'react';
import Card from '../common/Card/Card';
import Loader from '../common/Loader/Loader';
import Modal from '../common/Modal/Modal';
import { useUser } from '../../contexts/UserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApi } from '../../hooks/useApi';
import { hapticFeedback, openLink } from '../../utils/telegram';
import './Transactions.css';

// Import icons for navigation
import { RiHome5Line } from 'react-icons/ri';
import { RiFileList3Line } from 'react-icons/ri';
import { RiExchangeLine } from 'react-icons/ri';
import { RiVipCrownLine } from 'react-icons/ri';
import { RiUserShared2Line } from 'react-icons/ri';

const Transactions = () => {
  const { user, loading, refreshUserData } = useUser();
  const { t } = useLanguage();
  const { getTransactions, requestWithdrawal, createDeposit } = useApi();
  const [transactions, setTransactions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isWithdrawModalOpen, setIsWithdrawModalOpen] = useState(false);
  const [isDepositModalOpen, setIsDepositModalOpen] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [depositAmount, setDepositAmount] = useState('');
  const [withdrawCurrency, setWithdrawCurrency] = useState('USDT');
  const [depositCurrency, setDepositCurrency] = useState('USDT');
  const [withdrawAddress, setWithdrawAddress] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [filter, setFilter] = useState('all');
  const [error, setError] = useState(null);

  // Define filter options with translation keys
  const filterOptions = [
    { id: 'all', labelKey: 'allTransactions' },
    { id: 'DEPOSIT', labelKey: 'depositsFilter' },
    { id: 'WITHDRAWAL', labelKey: 'withdrawalsFilter' },
    { id: 'LICENSE_PURCHASE', labelKey: 'licensesFilter' },
    { id: 'LICENSE_UPGRADE', labelKey: 'upgradesFilter' },
    { id: 'REINVESTMENT', labelKey: 'reinvestFilter' },
    { id: 'REFERRAL_COMMISSION', labelKey: 'referralFilter' },
    { id: 'FOX_CONVERSION', labelKey: 'conversionsFilter' },
    { id: 'PROFIT_CONVERSION', labelKey: 'conversionsFilter' }
  ];

  useEffect(() => {
    async function fetchTransactions() {
      try {
        setIsLoading(true);
        setError(null);
        // Use real API call instead of mock data
        const data = await getTransactions({ type: filter !== 'all' ? filter : undefined });
        setTransactions(data);
      } catch (error) {
        console.error('Error fetching transactions:', error);
        setError(t('transactionLoadError'));
      } finally {
        setIsLoading(false);
      }
    }

    if (user) {
      fetchTransactions();
    }
  }, [user, getTransactions, filter]);

  const handleWithdrawClick = () => {
    hapticFeedback.medium();
    setIsWithdrawModalOpen(true);
  };

  const handleDepositClick = () => {
    hapticFeedback.medium();
    setIsDepositModalOpen(true);
  };

  const handleWithdrawSubmit = async () => {
    if (!withdrawAmount || parseFloat(withdrawAmount) < 100 || !withdrawAddress) {
      hapticFeedback.error();
      return;
    }

    // Проверяем, достаточно ли прибыли для вывода
    if (parseFloat(withdrawAmount) > (user?.totalProfit || 0)) {
      hapticFeedback.error();
      setError(t('insufficientProfitForWithdrawal'));
      return;
    }

    try {
      setIsProcessing(true);
      hapticFeedback.medium();
      await requestWithdrawal(parseFloat(withdrawAmount), withdrawCurrency, withdrawAddress);
      hapticFeedback.success();
      setIsWithdrawModalOpen(false);
      // Reset form
      setWithdrawAmount('');
      setWithdrawAddress('');
      // Refresh user data and transactions
      await refreshUserData();
      // Refresh transactions list
      const data = await getTransactions({ type: filter !== 'all' ? filter : undefined });
      setTransactions(data);
    } catch (error) {
      console.error('Withdrawal error:', error);
      hapticFeedback.error();
      setError(error.message || t('withdrawalRequestError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDepositSubmit = async () => {
    if (!depositAmount || parseFloat(depositAmount) <= 0) {
      hapticFeedback.error();
      return;
    }

    try {
      setIsProcessing(true);
      hapticFeedback.medium();
      const result = await createDeposit(parseFloat(depositAmount), depositCurrency);
      hapticFeedback.success();
      setIsDepositModalOpen(false);
      // Reset form
      setDepositAmount('');
      
      // If there's a payment URL, open it using Telegram WebApp API if available
      if (result.transaction?.details?.paymentUrl) {
        openLink(result.transaction.details.paymentUrl);
      }
      
      // Refresh transactions list
      const data = await getTransactions({ type: filter !== 'all' ? filter : undefined });
      setTransactions(data);
    } catch (error) {
      console.error('Deposit error:', error);
      hapticFeedback.error();
      setError(error.message || t('depositRequestError'));
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    hapticFeedback.light();
    setFilter(newFilter);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU').replace(/\./g, '.');
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusTranslation = (status) => {
    switch(status) {
      case 'PENDING': return t('inProcessing');
      case 'CONFIRMED': return t('confirmed');
      case 'FAILED': return t('failed');
      case 'COMPLETED': return t('completed');
      default: return status;
    }
  };

  const getTransactionTypeTranslation = (type) => {
    switch(type) {
      case 'DEPOSIT': return t('topUpTransaction');
      case 'WITHDRAWAL': return t('withdrawalTransaction');
      case 'LICENSE_PURCHASE': return t('licensePurchase');
      case 'LICENSE_UPGRADE': return t('licenseUpgrade');
      case 'REINVESTMENT': return t('reinvestment');
      case 'REFERRAL_COMMISSION': return t('referralCommission');
      case 'FOX_CONVERSION': return t('foxConversionTransaction');
      case 'PROFIT_CONVERSION': return t('profitConversionTransaction');
      default: return type;
    }
  };

  // Format amount with space separating thousands
  const formatAmount = (amount) => {
    return amount.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  };

  // Mock navigation handler
  const handleNavigation = (route) => {
    console.log(`Navigating to: ${route}`);
    // In a real app, you would use history.push or similar navigation
  };

  if (loading) {
    return <Loader fullPage={true} />;
  }

  return (
    <div className="transactions-page">
      <div className="page-header">
        <h1 className="transactions-title">{t('transactionHistory')}</h1>
        <div className="transaction-buttons">
          <button 
            className="deposit-button" 
            onClick={() => {
              hapticFeedback.medium();
              handleDepositClick();
            }}
          >
            {t('topUp')}
          </button>
          <button 
            className="withdraw-button" 
            onClick={() => {
              if (user && (user.totalProfit || 0) > 0) {
                hapticFeedback.medium();
              } else {
                hapticFeedback.light();
              }
              handleWithdrawClick();
            }}
            disabled={!user || (user.totalProfit || 0) <= 0}
          >
            {t('withdrawProfit')}
          </button>
        </div>
      </div>

      <div className="filter-tabs">
        {filterOptions.map(option => (
          <button
            key={option.id}
            className={`filter-tab ${filter === option.id ? 'active' : ''}`}
            onClick={() => {
              hapticFeedback.light();
              handleFilterChange(option.id);
            }}
          >
            {t(option.labelKey)}
          </button>
        ))}
      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {isLoading ? (
        <Loader />
      ) : (
        <div className="transactions-list">
          {transactions.length === 0 ? (
            <div className="empty-state">
              <p>{t('transactionsNotFound')}</p>
            </div>
          ) : (
            transactions.map(transaction => (
              <div key={transaction.id} className="transaction-card">
                <div className="transaction-date-row">
                  <div className="transaction-date">{formatDate(transaction.createdAt)}</div>
                  <div className="transaction-time">{formatTime(transaction.createdAt)}</div>
                </div>
                
                <div className="transaction-info-row">
                  <div className="transaction-type">
                    {getTransactionTypeTranslation(transaction.type)}
                  </div>
                  <div className="transaction-amount">
                    ${formatAmount(transaction.amount)}
                  </div>
                </div>
                
                <div className="transaction-status-row">
                  <div className="transaction-status-label">{t('status')}</div>
                  <div className={`transaction-status-value status-${transaction.status}`}>
                    {getStatusTranslation(transaction.status)}
                  </div>
                </div>
                
                {transaction.status === 'PENDING' && 
                 transaction.type === 'DEPOSIT' && 
                 transaction.details && 
                 transaction.details.paymentUrl && (
                  <div className="transaction-actions">
                    <button 
                      className="payment-button"
                      onClick={() => {
                        hapticFeedback.medium();
                        openLink(transaction.details.paymentUrl);
                      }}
                    >
                      {t('pay')}
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}

      <Modal
        isOpen={isWithdrawModalOpen}
        onClose={() => {
          hapticFeedback.light();
          setIsWithdrawModalOpen(false);
        }}
        title={t('withdrawModal')}
        actions={(
          <>
            <button 
              className="button secondary" 
              onClick={() => {
                hapticFeedback.light();
                setIsWithdrawModalOpen(false);
              }}
            >
              {t('cancel')}
            </button>
            <button 
              className="button primary" 
              onClick={() => {
                if (!isProcessing && withdrawAmount && withdrawAddress) {
                  hapticFeedback.medium();
                }
                handleWithdrawSubmit();
              }}
              disabled={isProcessing || !withdrawAmount || !withdrawAddress || parseFloat(withdrawAmount) < 100 || parseFloat(withdrawAmount) > (user?.totalProfit || 0)}
            >
              {isProcessing ? t('processing') : t('withdraw')}
            </button>
          </>
        )}
      >
        <div className="form-group">
          <label>{t('amountToWithdrawLabel')}</label>
          <input
            type="number"
            value={withdrawAmount}
            onChange={(e) => setWithdrawAmount(e.target.value)}
            placeholder={t('enterAmount')}
            min="100"
            step="0.01"
            max={user?.totalProfit || 0}
          />
          <div className="form-hint">
            {t('availableForWithdrawal')}: {user?.totalProfit || 0} USDT<br />
            {t('minimumWithdrawal')}: 100 USDT
          </div>
        </div>
        <div className="form-group">
          <label>{t('currency')}</label>
          <select
            value={withdrawCurrency}
            onChange={(e) => setWithdrawCurrency(e.target.value)}
          >
            <option value="USDT">USDT</option>
            <option value="BTC">BTC</option>
            <option value="ETH">ETH</option>
          </select>
        </div>
        <div className="form-group">
          <label>{t('walletAddress')}</label>
          <input
            type="text"
            value={withdrawAddress}
            onChange={(e) => setWithdrawAddress(e.target.value)}
            placeholder={t('enterAddress')}
          />
        </div>
      </Modal>

      <Modal
        isOpen={isDepositModalOpen}
        onClose={() => {
          hapticFeedback.light();
          setIsDepositModalOpen(false);
        }}
        title={t('depositAccountTitle')}
        actions={(
          <>
            <button 
              className="button secondary" 
              onClick={() => {
                hapticFeedback.light();
                setIsDepositModalOpen(false);
              }}
            >
              {t('cancel')}
            </button>
            <button 
              className="button primary" 
              onClick={() => {
                if (!isProcessing && depositAmount) {
                  hapticFeedback.medium();
                }
                handleDepositSubmit();
              }}
              disabled={isProcessing || !depositAmount}
            >
              {isProcessing ? t('processing') : t('deposit')}
            </button>
          </>
        )}
      >
        <div className="form-group">
          <label>{t('amount')}</label>
          <input
            type="number"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            placeholder={t('enterAmount')}
            min="1"
            step="0.01"
          />
        </div>
        <div className="form-group">
          <label>{t('currency')}</label>
          <select
            value={depositCurrency}
            onChange={(e) => setDepositCurrency(e.target.value)}
          >
            <option value="USDT">USDT</option>
            <option value="BTC">BTC</option>
            <option value="ETH">ETH</option>
          </select>
        </div>
      </Modal>
    </div>
  );
};

export default Transactions; 